// dependencies
import cn from 'classnames';
import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// pages
import HomePage from '@/pages/scheduling/HomePage';
import NeedsAttentionPage from '@/pages/scheduling/NeedsAttentionPage';
import AllocationsPage from '@/pages/scheduling/allocations/AllocationsPage';
import ClientsPage from '@/pages/scheduling/clients/ClientsPage';
import ClientDetailsPage from '@/pages/scheduling/clients/ClientDetailsPage';
import ClientsTestPage from '@/pages/scheduling/clients/ClientsTestPage';
import EngagementsPage from '@/pages/scheduling/engagements/EngagementsPage';
import EngagementDetailsPage from '@/pages/scheduling/engagements/EngagementDetailsPage';
import ProfessionalsPage from '@/pages/scheduling/professionals/ProfessionalsPage';
import ProfessionalDetailsPage from '@/pages/scheduling/professionals/ProfessionalDetailsPage';
import ProfessionalLoadPage from '@/pages/scheduling/professionals/ProfessionalLoadPage';
import ChargeabilityPage from '@/pages/scheduling/chargeability/ChargeabilityPage';
import RequestsPage from '@/pages/scheduling/requests/RequestsPage';
import RecruitmentPage from '@/pages/scheduling/recruitment/RecruitmentPage';
import HoursReportPage from '@/pages/scheduling/hours/HoursReportPage';
import ManagementHoursPage from '@/pages/scheduling/hours/ManagementHoursPage';
import LoginPage from '@/pages/users/LoginPage';
import FileUploadPage from '@/pages/scheduling/file/fileUploadPage';
import MaintenancePage from '@/pages/scheduling/Maintenance';
import DraftsPage from '@/pages/scheduling/drafts/DraftsPage';
import AdminPage from '@/pages/scheduling/AdminPage';

import Error404 from '@/pages/users/Error404';
// components
import { ActionModal } from '@digital_monorepo/lib-common';
import Footer from '@/components/Footer';
import AppHeader from '@/components/common/AppHeader';
import Chatbot from '@/components/Chatbot';
import SessionTimeoutModal from '@/components/SessionTimeoutModal';
// hooks
import useRedux from '@/hooks/useRedux';
// utils
import { PAGES } from './utils/navigation/constants';
// frameworks
import ErrorService from '@/frameworks/error';
// styling
import { styles } from '@digital_monorepo/styling';
// services
import { getProcessingFileStatus } from '@/services/schedulingService'; // Import the getProcessingStatus function

const App = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(null);
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false); // State to track maintenance status
  const [isAdmin, setIsAdmin] = useState(false);

  const { getAuthData, logout, cleanErrors, checkSession } = useRedux();
  let { isLoggedIn, name, language, role, timeout } = getAuthData();
  const error = ErrorService(navigate, location, logout);

  const isAllocationsPage = location.pathname === '/allocations';
  const isRequestsPage = location.pathname === '/requests';
  const isChargeabilityPage = location.pathname === '/chargeability';
  // Pages that manage their own scrolling and fill the viewport.
  const isFullHeightPage = isAllocationsPage || isChargeabilityPage;

  //isLoggedIn = true;
  // Check maintenance status before loading the app

  // On every page load, validate the stored session with the backend.
  // If the backend restarted or the token expired, doCheckSession will
  // clear localStorage and log the user out, sending them to /login.
  useEffect(() => {
    if (isLoggedIn) {
      checkSession();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When isLoggedIn drops to false (e.g. session invalidated after backend
  // restart) redirect to /login so the user sees the login form instead of
  // a blank page caused by the authenticated routes no longer rendering.
  useEffect(() => {
    if (!isLoggedIn && location.pathname !== '/login' && location.pathname !== '/maintenance') {
      navigate('/login');
    }
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkMaintenanceStatus = async () => {
      try {
        const statusResponse = await getProcessingFileStatus();
        const status = statusResponse[0].status === 'active'; // Access the 'status' property
        if (status) {
          setIsUnderMaintenance(true);
          navigate('/maintenance'); // Redirect to maintenance page
        }
      } catch (error) {
        console.error('Error checking maintenance status:', error);
      }
    };

    checkMaintenanceStatus();
  }, [navigate]);

  useEffect(() => {
    setIsAdmin(role === 'ADMIN');
  }, [role]);

  useEffect(() => {
    i18n.changeLanguage('en');
  }, [language, isAdmin]);

  useEffect(() => {
    if (location.pathname) {
      const segment = location.pathname.split('/')[1];
      let page = PAGES.find(p => segment === p.path.split('/')[1] || p.children?.some(c => segment === c.path.split('/')[1]));
      setCurrentPage(page);
    }
  }, [location.pathname]);

  if (isUnderMaintenance) {
    return <MaintenancePage />; // Render the maintenance page if under maintenance
  }

  return (
    <>
      <a className='skip-link' href='#main-content'>
        Skip to content
      </a>
      <div className={cn(styles.container, 'workspace', isFullHeightPage && 'workspace-dense')} data-page={location.pathname.split('/')[1] || 'overview'}>
        {currentPage?.nav && isLoggedIn && <AppHeader name={name} isAdmin={isAdmin} logout={logout} />}
        <div className={cn(styles.rightContainer, currentPage?.nav ? styles.small : null)} style={isFullHeightPage ? { height: '100dvh', overflow: 'hidden', boxSizing: 'border-box' } : undefined}>
          <main id='main-content' tabIndex={-1} className={currentPage?.nav ? styles.pageBody : null} style={isFullHeightPage ? { marginTop: 0, marginBottom: 0, height: '100%' } : undefined}>
            <Routes>
              {isLoggedIn && (
                <>
                  <Route exact path='/' element={<HomePage />} />
                  <Route exact path='/summary' element={<NeedsAttentionPage />} />
                  <Route exact path='/allocations' element={<AllocationsPage />} />
                  <Route exact path='/allocations-classic' element={<Navigate to='/allocations' replace />} />
                  <Route exact path='/clients' element={<ClientsPage />} />
                  <Route exact path='/clients-test' element={<ClientsTestPage />} />
                  <Route exact path='/clients/:clientCode' element={<ClientDetailsPage />} />
                  <Route exact path='/engagements' element={<EngagementsPage />} />
                  <Route exact path='/engagements/:id' element={<EngagementDetailsPage />} />
                  <Route exact path='/professionals' element={<ProfessionalsPage />} />
                  <Route exact path='/professionals/:id' element={<ProfessionalDetailsPage />} />
                  <Route exact path='/chargeability' element={<ChargeabilityPage />} />
                  <Route exact path='/requests' element={<RequestsPage />} />
                  <Route exact path='/recruitment' element={<RecruitmentPage />} />
                  <Route exact path='/hours-report' element={<HoursReportPage />} />
                  <Route exact path='/file-upload' element={<FileUploadPage />} />
                  <Route exact path='/drafts' element={<DraftsPage />} />
                  {isAdmin && <Route exact path='/admin' element={<AdminPage />} />}
                  {isAdmin && <Route exact path='/professional-load' element={<ProfessionalLoadPage />} />}
                </>
              )}
              <Route exact path='/login' element={<LoginPage />} />
              <Route exact path='/maintenance' element={<MaintenancePage />} /> {/* Add a maintenance page route */}
              <Route path='*' element={<Error404 />} />
            </Routes>

            {/* ERROR */}
            {error.hasError && error.state !== 'leads' && (
              <ActionModal
                open={error.hasError}
                i18nPath='ERROR.ERROR_MODAL'
                onClose={() => {
                  cleanErrors();
                  navigate('/');
                }}
                yesAction={() => {
                  cleanErrors();
                  navigate('/');
                }}
              />
            )}

            {/* Session timeout modal */}
            {isLoggedIn && timeout && <SessionTimeoutModal timeout={timeout} logout={logout} />}
          </main>
          {currentPage?.nav && !isFullHeightPage && !isRequestsPage && <Footer timeout={timeout} />}
        </div>
        {isLoggedIn && <Chatbot />}
      </div>
    </>
  );
};

export default App;
